import { functions, httpsCallable } from './initFirebase.js';
import { isAdmin } from './_default.js';

const translations = {
  language: null,
  availableLanguages: ['en', 'de'],
  fallbackLanguage: 'en',
  loadedTranslations: {},

  init: async function(){
    if(localStorage.getItem('language')){
      translations.language = localStorage.getItem('language');
    }else{
      translations.setBrowserLangague();
    }
    await translations.getTranslation();
  },

  getLanguageAsString: function(){
    switch (translations.language) {
      case 'en': return 'english';
      case 'de': return 'deutsch';
    }
  },

  setLanguage: function(language){
    translations.language = language;
    localStorage.setItem('language', language);
  },

  setBrowserLangague: function(){
    let language = navigator.language || navigator.userLanguage;
    language = language.split('-')[0];
    if(!translations.availableLanguages.includes(language)){
      language = fallbackLanguage;
    }
    translations.setLanguage(language);
  },

  getString: function(partKeyPair, stringReplacements, overwriteLanguage, returnEmptyString){
    const partKeyPairArray = partKeyPair.split('.');
    const part = partKeyPairArray[0];
    const key = partKeyPairArray[1];
    if(!translations.loadedTranslations[part] || !translations.loadedTranslations[part][key]){
      if(returnEmptyString){
        return '';
      }else{
        return '${'+partKeyPair+'}';
      }
    }
    let language = overwriteLanguage || translations.language;
    let string = translations.loadedTranslations[part][key][language];
    if(string === null){
      if(returnEmptyString){
        return '';
      }else{
        return '${'+partKeyPair+'}';
      }
    }
    if(stringReplacements && stringReplacements.length){
      stringReplacements.forEach((stringReplacement) => {
        string = string.replace(stringReplacement.key, String(stringReplacement.value));
      });
    }
    return string;
  },

  getDateString: function(date, flavor) {
    if (!date) {
      return false;
    }
    if (typeof (date) === 'string') {
      date = new Date(date);
    }

    if (typeof (date._seconds) !== 'undefined') {
      date = new Date(date._seconds * 1000);
    }

    const now = new Date();
    const diffInMs = now - date; // Difference in milliseconds
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    const language = translations.language === 'de' ? 'de' : 'en'; // map language to 'de' or 'en'

    // If 'ago' flavor is specified
    if (flavor === 'AGO') {
      if (language === 'de') {
        if (diffInSeconds < 60) {
          return `vor ${diffInSeconds} Sekunden`;
        } else if (diffInMinutes < 60) {
          return `vor ${diffInMinutes} Minuten`;
        } else if (diffInHours < 24) {
          return `vor ${diffInHours} Stunden`;
        } else {
          return `vor ${diffInDays} Tagen`;
        }
      } else {
        if (diffInSeconds < 60) {
          return `${diffInSeconds} sec ago`;
        } else if (diffInMinutes < 60) {
          return `${diffInMinutes} min ago`;
        } else if (diffInHours < 24) {
          return `${diffInHours} hours ago`;
        } else {
          return `${diffInDays} days ago`;
        }
      }
    }

    // If 'input' flavor is specified, return the input date
    if (flavor === 'INPUT') {
      // Return for input form like 2021-01-01
      return date.toISOString().split('T')[0];
    }

    // Regular date formatting
    let options = { day: 'numeric', month: 'long', year: 'numeric' };

    if (flavor === 'SHORT') {
      options = { month: 'short', year: 'numeric' };
    }

    const locale = language === 'de' ? 'de-DE' : 'en-US'; // map language to locale
    return date.toLocaleDateString(locale, options);
  },

  getTranslation: async function(){
    let part;

    if(location.pathname.includes('/admin/')){
      part = 'admin,profile,languages,app,activities,errors,questions';
    }else if(location.pathname.includes('/onboarding')){
      part = 'onboarding,app';
    }else if(location.pathname.includes('/app') || location.pathname.includes('/profile')){
      part = 'app,profile,languages,errors,auth,questions';
    }else if(location.pathname.includes('/login') || location.pathname.includes('/signup') || location.pathname.includes('/verify')){
      part = 'auth,errors';
    }else if(location.pathname === '/'){
      part = 'landing';
    }else{
      // Loading for landing for unknown pages (404)
      part = 'landing,errors';
    }

    // Get all languges for admin (needed for questions)
    const language = isAdmin ? 'de,en' : translations.language;

    try {
      const getTranslationResult = await httpsCallable(functions, 'getTranslation')({
        part,
        language
      });
      translations.loadedTranslations = getTranslationResult.data.translations;
      translations.translatePage();

    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  translatePage: function() {
    const elements = document.querySelectorAll('*:not(script):not(style)'); // Select all elements except <script> and <style>
    elements.forEach(element => {
      // Replace text content that matches the ${key} pattern
      if (element.childNodes.length === 1 && element.childNodes[0].nodeType === Node.TEXT_NODE) {
        let text = element.textContent.replace(/\$\{([a-zA-Z0-9_\.]+)\}/g, (_, key) => {
          let translation = translations.loadedTranslations;
          const keyParts = key.split('.');
          for (let part of keyParts) {
            if (translation && translation[part]) {
              translation = translation[part];
            } else {
              return `\$\{${key}\}`;
            }
          }
          return translation && translation[translations.language] ? translation[translations.language] : `\$\{${key}\}`;
        });

        // Check if markdown links exist, checks https://, mailto:, and /
        const containsMarkdownLinks = /\[([^\]]+)\]\((https?:\/\/[^\)]+|mailto:[^\)]+|\/[^\)]+)\)/.test(text);

        // If markdown links are found, convert them to HTML <a> tags
        if (containsMarkdownLinks) {
          text = text.replace(/\[([^\]]+)\]\((https?:\/\/[^\)]+|mailto:[^\)]+|\/[^\)]+)\)/g, (match, linkText, url) => {
            return `<a href="${url}" target="_blank">${linkText}</a>`;
          });
          element.innerHTML = text; // Use innerHTML when markdown links are present
        } else {
          element.textContent = text; // Use textContent for normal text
        }
      }
    });

    // Meta tags
    if(translations.loadedTranslations['landing']){
      const obj = translations.loadedTranslations['landing'];
      if(obj['meta_title'] && obj['meta_title'][translations.language]){
        document.querySelector('meta[property="og:title"]').setAttribute('content', obj['meta_title'][translations.language]);
      }
      if(obj['meta_description'] && obj['meta_description'][translations.language]){
        document.querySelector('meta[property="og:description"]').setAttribute('content', obj['meta_description'][translations.language]);
      }
    }
  }
}

export { translations };